import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import {
  getTeamBalance,
  TeamBalancePayload,
} from '../../../services/teamBalance'

const EMPTY_PAYLOAD: TeamBalancePayload = { team_registrations: [] }

interface UseTeamBalanceOptions {
  teamId?: string | null
  /**
   * D40 — the sheet is roster-scoped. Part of the cache key, so switching
   * rosters can never serve the previous roster's sheet from cache.
   */
  rosterId?: string | null
  enabled?: boolean
}

/**
 * SN-4709 balance read for ONE roster's sheet (D40).
 *
 * Tab GATING is no longer this hook's job — the sheet needs a roster, and the
 * team-wide "do I have any balance here?" question is answered by
 * useBalanceRosters (the discovery probe). A 403 still resolves silently to an
 * empty registration list; other errors surface so the tab body can offer a
 * retry. A 404 (unknown / cross-team roster) is a real error and surfaces —
 * deliberately distinct from an own-team roster with nothing visible, which the
 * server answers 200 + [] (A-5).
 */
export const useTeamBalance = ({
  teamId,
  rosterId,
  enabled = true,
}: UseTeamBalanceOptions) => {
  const query = useQuery<TeamBalancePayload>({
    queryKey: queryKeys.teamBalance.byTeamRoster(teamId, rosterId),
    queryFn: async () => {
      try {
        return await getTeamBalance(teamId as string, rosterId as string)
      } catch (error) {
        const status =
          (error as { status?: number; statusCode?: number })?.status ??
          (error as { status?: number; statusCode?: number })?.statusCode
        if (status === 403) {
          return EMPTY_PAYLOAD
        }
        throw error
      }
    },
    enabled: Boolean(teamId) && Boolean(rosterId) && enabled,
    retry: false,
    staleTime: 30_000,
    // Money moves out-of-band (a player pays, a scheduled charge lands) — refetch
    // when the rep returns to the tab so KPIs/statuses stay fresh. Overrides the
    // app-wide refetchOnWindowFocus: false. This is now the ONLY automatic path
    // back to fresh data besides mutation invalidation — the manual Refresh
    // button was removed, so do not lower this to false without replacing it.
    refetchOnWindowFocus: true,
  })

  return {
    ...query,
    registrations: query.data?.team_registrations ?? [],
  }
}

export default useTeamBalance
